import React from 'react';
import { useTranslation } from 'react-i18next';
import AnimatedDots from '@/components/shared/AnimatedDots';
import type { ImportStatusResponse } from 'we-tree-api-client/v2';

interface GedcomUploadButtonProps {
  onClick: () => void;
  disabled: boolean;
  uploadState: string;
  hasActiveImport: boolean;
  importStatus: ImportStatusResponse | null;
}

/**
 * GedcomUploadButton - Button component for GEDCOM file upload with status display
 * Shows different states: idle, uploading, processing with status, completed
 */
const GedcomUploadButton: React.FC<GedcomUploadButtonProps> = ({
  onClick,
  disabled,
  uploadState,
  hasActiveImport,
  importStatus
}) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');

  const hasCompletedStatus =
    importStatus && ['completed', 'failed', 'cancelled', 'rolled_back'].includes(importStatus.status);
  const isProcessing = uploadState === 'uploading' || hasActiveImport || !!hasCompletedStatus;

  const getButtonContent = () => {
    if (isProcessing && importStatus) {
      // Show status with animated dots (only show dots if not completed)
      return (
        <>
          <span className="gedcom-upload-page__status-text">{importStatus.status}</span>
          {!hasCompletedStatus && <AnimatedDots />}
        </>
      );
    } else if (uploadState === 'uploading') {
      // Uploading file - show uploading with dots
      return (
        <>
          {t('gedcomUpload.uploading')}
          <AnimatedDots />
        </>
      );
    } else if (isProcessing) {
      // Processing but no status yet - show generic message with dots
      return (
        <>
          {t('gedcomUpload.processing')}
          <AnimatedDots />
        </>
      );
    } else {
      // Default state
      return tCommon('upload');
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="btn btn--primary gedcom-upload-page__upload-btn"
    >
      {getButtonContent()}
    </button>
  );
};

export default GedcomUploadButton;
